import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ResetPasswordDialog } from './ResetPasswordDialog';
import { RememberCredentialsDialog } from './RememberCredentialsDialog';
import {
  loadCredentials,
  saveCredentials,
  hasDeclinedRemember,
  setDeclinedRemember,
  hasStoredCredentials,
  isCredentialStorageAvailable,
} from '@/utils/credentialStorage';

export const SignInForm = () => {
  // Safe navigation - use window.location as fallback if router context is not available
  let navigate;
  try {
    navigate = useNavigate();
  } catch (error) {
    console.warn('Router context not available, using window.location fallback');
    navigate = (path: string) => {
      window.location.href = path;
    };
  }
  
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [signInError, setSignInError] = useState('');
  const [showRememberDialog, setShowRememberDialog] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null);

  // Load saved credentials on mount (native only)
  useEffect(() => {
    if (!isCredentialStorageAvailable()) return;

    const load = async () => {
      try {
        const creds = await loadCredentials();
        if (creds) {
          setEmail(creds.email);
          setPassword(creds.password);
        }
      } catch (err) {
        console.warn('Failed to load saved credentials:', err);
      }
    };
    load();
  }, []);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setSignInError('');

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        if (error.message === 'Invalid login credentials') {
          setSignInError('Incorrect email or password. Please try again.');
        } else {
          setSignInError(error.message);
        }
        throw error;
      }

      // On native platforms, check if we should prompt to remember credentials
      if (isCredentialStorageAvailable()) {
        const alreadySaved = await hasStoredCredentials();
        const declined = await hasDeclinedRemember();

        if (!alreadySaved && !declined) {
          // Show the remember credentials dialog before navigating
          setPendingCredentials({ email, password });
          setShowRememberDialog(true);
          return; // Don't navigate yet — wait for dialog response
        }

        // If already saved, update with latest credentials silently
        if (alreadySaved) {
          await saveCredentials(email, password);
        }
      }

      navigate('/');
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemember = async () => {
    if (pendingCredentials) {
      await saveCredentials(pendingCredentials.email, pendingCredentials.password);
    }
    setShowRememberDialog(false);
    setPendingCredentials(null);
    navigate('/');
  };

  const handleDecline = async () => {
    await setDeclinedRemember();
    setShowRememberDialog(false);
    setPendingCredentials(null);
    navigate('/');
  };

  return (
    <>
      <Card className="p-4 sm:p-6 w-full bg-background">
        <form onSubmit={handleSignIn} className="space-y-4" autoComplete="on">
          {signInError && (
            <Alert variant="destructive">
              <AlertDescription>{signInError}</AlertDescription>
            </Alert>
          )}
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="w-full"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
              Password
            </label>
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="w-full"
            />
          </div>

          <div className="flex justify-between items-center">
            <Button 
              type="button" 
              variant="link" 
              className="px-0 text-sm"
              onClick={() => setShowResetDialog(true)}
            >
              Forgot password?
            </Button>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? "Signing in..." : "Sign In"}
          </Button>
        </form>
      </Card>

      <ResetPasswordDialog 
        open={showResetDialog} 
        onOpenChange={setShowResetDialog} 
      />

      <RememberCredentialsDialog
        open={showRememberDialog}
        onRemember={handleRemember}
        onDecline={handleDecline}
      />
    </>
  );
};
